import t from "@/lang/lang";
import type {
  AssetRefDTO,
  MonitorAdditionalDTO,
  MonitorSnapDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { useQpmMonitorShowImageQuery } from "@/services/backend/qpm/monitors/api/monitor-api";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { ImageUp } from "lucide-react";
import type { ReactNode } from "react";

type DiffRow = {
  label: string;
  current: DiffCellValue;
  proposed: DiffCellValue;
};

type DiffCellValue = string | DiffValue;

type DiffValue = {
  compare: string;
  render: ReactNode;
};

type RentingRow = {
  duration: string;
  current: string;
  proposed: string;
};

type DetailsRow = {
  key: string;
  current: string;
  proposed: string;
};

type SnapshotSectionKind =
  | { kind: "diff"; title: string; rows: DiffRow[] }
  | { kind: "details"; title: string; rows: DetailsRow[] }
  | { kind: "renting"; title: string; rows: RentingRow[] };

function safeJson(value: unknown) {
  if (value === undefined) return "--";
  if (value === null) return "null";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function toStr(value: unknown) {
  if (value === undefined || value === null) return "--";
  if (typeof value === "boolean") return value ? t("Ja") : t("Nein");
  return String(value);
}

function toNumberStr(value: unknown) {
  if (value === undefined || value === null) return "--";
  if (typeof value === "number") return String(value);
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value);
}

function toValueString(value: unknown) {
  if (value === undefined || value === null) return "--";
  if (typeof value === "boolean") return value ? t("Ja") : t("Nein");
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return safeJson(value);
}

function textValue(value: string): DiffValue {
  return {
    compare: value,
    render: value,
  };
}

function normalizeDiffValue(value: DiffCellValue): DiffValue {
  return typeof value === "string" ? textValue(value) : value;
}

function imageRefValue(image: AssetRefDTO | undefined): DiffValue {
  return {
    compare: imageRefMeta(image),
    render: <ImagePreview image={image} />,
  };
}

function imageRefMeta(image: AssetRefDTO | undefined) {
  const fileId = image?.fileUuid ?? image?.fileId;
  if (!fileId) return "—";
  return `${fileId}${image?.prefix ? ` ${image.prefix}` : ""}`;
}

function ImagePreview({ image }: { image?: AssetRefDTO }) {
  const fileId = image?.fileUuid ?? image?.fileId ?? "";
  const { data, isFetching, isError } = useQpmMonitorShowImageQuery(
    {
      fileId,
      filePrefix: image?.prefix ?? "",
    },
    {
      skip: !fileId || !image?.prefix,
    },
  );

  if (!fileId || !image?.prefix) {
    return <span>—</span>;
  }
  if (isFetching) {
    return <span className="text-muted-foreground">{t("Lädt")}</span>;
  }
  if (isError || !data?.objectURL) {
    return (
      <span className="text-muted-foreground flex items-center gap-2">
        <ImageUp className="h-4 w-4" />
        {t("Bild konnte nicht geladen werden")}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <img
        src={data.objectURL}
        alt={t("Bild")}
        className="border-border h-20 w-28 rounded border object-contain"
      />
    </div>
  );
}

function getBool(obj: unknown, key: string): boolean | undefined {
  if (!obj || typeof obj !== "object") return undefined;
  return (obj as Record<string, unknown>)[key] as boolean | undefined;
}

function getPath(obj: unknown, path: string[]): unknown {
  return path.reduce<unknown>((cur, p) => {
    if (!cur || typeof cur !== "object") return undefined;
    return (cur as Record<string, unknown>)[p];
  }, obj);
}

function DiffTable({ rows }: { rows: DiffRow[] }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-55">{t("Feld")}</TableHead>
            <TableHead>{t("Aktuell")}</TableHead>
            <TableHead>{t("Überschreiben mit")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const current = normalizeDiffValue(r.current);
            const proposed = normalizeDiffValue(r.proposed);
            const changed = current.compare !== proposed.compare;
            const rowClassName = changed ? "bg-emerald-500/10" : undefined;
            const cellClassName = changed ? "font-medium" : undefined;

            return (
              <TableRow key={r.label} className={rowClassName}>
                <TableCell className="align-top font-medium">
                  {r.label}
                </TableCell>
                <TableCell
                  className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
                >
                  {current.render}
                </TableCell>
                <TableCell
                  className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
                >
                  {proposed.render}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function DetailsTable({ rows }: { rows: DetailsRow[] }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-55">{t("Feld")}</TableHead>
            <TableHead>{t("Wert")}</TableHead>
            <TableHead>{t("Wert")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const changed = r.current !== r.proposed;
            const rowClassName = changed ? "bg-emerald-500/10" : undefined;
            const cellClassName = changed ? "font-medium" : undefined;

            return (
              <TableRow key={r.key} className={rowClassName}>
                <TableCell className="align-top font-medium">{r.key}</TableCell>
                <TableCell
                  className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
                >
                  {r.current}
                </TableCell>
                <TableCell
                  className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
                >
                  {r.proposed}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function RentingDefaultsTable({ rows }: { rows: RentingRow[] }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-55">{t("Dauer")}</TableHead>
            <TableHead>{t("Aktuell")}</TableHead>
            <TableHead>{t("Überschreiben mit")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const changed = r.current !== r.proposed;
            const rowClassName = changed ? "bg-emerald-500/10" : undefined;
            const cellClassName = changed ? "font-medium" : undefined;

            return (
              <TableRow key={r.duration} className={rowClassName}>
                <TableCell className="align-top font-medium">
                  {r.duration}
                </TableCell>
                <TableCell
                  className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
                >
                  {r.current}
                </TableCell>
                <TableCell
                  className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
                >
                  {r.proposed}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function buildDetailsKeyLabel(key: string) {
  const labels: Record<string, string> = {
    description: t("Beschreibung"),
    installationType: t("Installationsart"),
    heightMM: t("Höhe in mm"),
    widthMM: t("Breite in mm"),
    depthMM: t("Tiefe in mm"),
    weightG: t("Gewicht in g"),
    coverageValue: t("Wirkungsbereich Wert"),
    coveragePreText: t("Wirkungsbereich Text davor"),
    coveragePostText: t("Wirkungsbereich Text danach"),
    starterSpecification: t("Starter Spezifikation"),
    protectionLevel: t("Schutzstufe"),
  };

  return labels[key] ?? key;
}

function buildRentingDefaultsRows(
  current: MonitorSnapDTO,
  proposed: MonitorSnapDTO,
): RentingRow[] {
  const cur = (
    current as unknown as { rentingDefaults?: Record<string, number> }
  ).rentingDefaults;
  const prop = (
    proposed as unknown as { rentingDefaults?: Record<string, number> }
  ).rentingDefaults;

  const keys = Array.from(
    new Set([
      ...(cur ? Object.keys(cur) : []),
      ...(prop ? Object.keys(prop) : []),
    ]),
  ).sort((a, b) => Number(a) - Number(b));

  return keys.map((duration) => ({
    duration,
    current: toNumberStr(cur?.[duration]),
    proposed: toNumberStr(prop?.[duration]),
  }));
}

function buildDetailsRows(current: MonitorSnapDTO, proposed: MonitorSnapDTO) {
  const cur = current.details as unknown as Record<string, unknown> | undefined;
  const prop = proposed.details as unknown as
    Record<string, unknown> | undefined;

  const keys = Array.from(
    new Set([
      ...(cur ? Object.keys(cur) : []),
      ...(prop ? Object.keys(prop) : []),
    ]),
  )
    .filter((k) => k !== "description")
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  return keys.map((key) => ({
    key: buildDetailsKeyLabel(key),
    current: toValueString(cur?.[key]),
    proposed: toValueString(prop?.[key]),
  }));
}

type ConsumableRow = {
  name: string;
  type: string;
  unit: string;
  quantityPerDevice: string;
  cadence: string;
  customAmountOfMeasures: string;
  basePrice: string;
  note: string;
  overridePrice: string;
  spec: string;
};

type AdditionalRow = {
  key: string;
  name: string;
  description: string;
  basePrice: string;
  rentingFees: string;
  image?: AssetRefDTO;
};

function flattenConsumableRows(
  snap: MonitorSnapDTO | undefined | null,
): ConsumableRow[] {
  const list = (snap as unknown as { consumables?: unknown[] } | undefined)
    ?.consumables;
  if (!Array.isArray(list)) return [];

  return list.map((link) => {
    const l = link as Record<string, unknown>;
    const consumable =
      (l.consumable as Record<string, unknown> | undefined) ?? {};
    const spec = consumable.spec as Record<string, unknown> | undefined;

    const specStr =
      spec && typeof spec === "object"
        ? Object.entries(spec)
            .filter(([, v]) => v !== undefined && v !== null && v !== "")
            .map(
              ([k, v]) =>
                `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`,
            )
            .join(", ")
        : "";

    const overridePrice = (
      l.overridePrice as Record<string, unknown> | undefined
    )?.price;
    const basePrice = (
      consumable.basePrice as Record<string, unknown> | undefined
    )?.price;

    return {
      name: toStr(consumable.name),
      type: toStr(consumable.type),
      unit: toStr(consumable.unit),
      quantityPerDevice: toValueString(l.quantityPerDevice ?? l.perDevice),
      cadence: toValueString(l.cadence),
      customAmountOfMeasures: toValueString(l.customAmountOfMeasures),
      basePrice:
        basePrice === undefined || basePrice === null
          ? "—"
          : toValueString(basePrice),
      note: toStr(l.note),
      overridePrice:
        overridePrice === undefined || overridePrice === null
          ? "—"
          : toValueString(overridePrice),
      spec: specStr || "—",
    };
  });
}

function rowKeyForConsumable(r: ConsumableRow) {
  return `${r.name}|${r.type}|${r.unit}|${r.spec}`;
}

function ConsumablesDiffTable({
  current,
  proposed,
}: {
  current: MonitorSnapDTO;
  proposed: MonitorSnapDTO;
}) {
  const currentRows = flattenConsumableRows(current);
  const proposedRows = flattenConsumableRows(proposed);

  const byKeyCurrent = new Map(
    currentRows.map((r) => [rowKeyForConsumable(r), r]),
  );
  const byKeyProposed = new Map(
    proposedRows.map((r) => [rowKeyForConsumable(r), r]),
  );

  const keys = Array.from(
    new Set([...byKeyCurrent.keys(), ...byKeyProposed.keys()]),
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  const merged = keys.map((k) => ({
    key: k,
    current: byKeyCurrent.get(k) ?? {
      name: "—",
      type: "—",
      unit: "—",
      quantityPerDevice: "—",
      cadence: "—",
      customAmountOfMeasures: "—",
      basePrice: "—",
      note: "—",
      overridePrice: "—",
      spec: "—",
    },
    proposed: byKeyProposed.get(k) ?? {
      name: "—",
      type: "—",
      unit: "—",
      quantityPerDevice: "—",
      cadence: "—",
      customAmountOfMeasures: "—",
      basePrice: "—",
      note: "—",
      overridePrice: "—",
      spec: "—",
    },
  }));

  const renderFieldRow = (
    label: string,
    cur: string,
    prop: string,
    rowId: string,
  ) => {
    const changed = cur !== prop;
    const rowClassName = changed ? "bg-emerald-500/10" : undefined;
    const cellClassName = changed ? "font-medium" : undefined;

    return (
      <TableRow key={rowId} className={rowClassName}>
        <TableCell className="align-top font-medium">{label}</TableCell>
        <TableCell
          className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
        >
          {cur}
        </TableCell>
        <TableCell
          className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
        >
          {prop}
        </TableCell>
      </TableRow>
    );
  };

  if (merged.length === 0) {
    return (
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-55">{t("Feld")}</TableHead>
              <TableHead>{t("Aktuell")}</TableHead>
              <TableHead>{t("Überschreiben mit")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={3}
                className="text-muted-foreground text-center"
              >
                {t("Keine Verbrauchsmaterialien")}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {merged.map(({ key, current: cur, proposed: prop }, idx) => (
        <div key={key} className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-55">{t("Feld")}</TableHead>
                <TableHead>{t("Aktuell")}</TableHead>
                <TableHead>{t("Überschreiben mit")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderFieldRow(
                t("Material"),
                cur.name,
                prop.name,
                `${idx}-name`,
              )}
              {renderFieldRow(t("Art"), cur.type, prop.type, `${idx}-type`)}
              {renderFieldRow(t("Einheit"), cur.unit, prop.unit, `${idx}-unit`)}
              {renderFieldRow(
                t("Eigenschaften"),
                cur.spec,
                prop.spec,
                `${idx}-spec`,
              )}
              {renderFieldRow(
                t("Benötigte Menge"),
                cur.quantityPerDevice,
                prop.quantityPerDevice,
                `${idx}-quantity`,
              )}
              {renderFieldRow(
                t("Kadenz"),
                cur.cadence,
                prop.cadence,
                `${idx}-cadence`,
              )}
              {renderFieldRow(
                t("Eigene Anzahl Maßnahmen"),
                cur.customAmountOfMeasures,
                prop.customAmountOfMeasures,
                `${idx}-customAmount`,
              )}
              {renderFieldRow(
                t("Basispreis"),
                cur.basePrice,
                prop.basePrice,
                `${idx}-basePrice`,
              )}
              {renderFieldRow(
                t("Überschreibungspreis"),
                cur.overridePrice,
                prop.overridePrice,
                `${idx}-override-price`,
              )}
              {renderFieldRow(
                t("Bemerkung"),
                cur.note,
                prop.note,
                `${idx}-note`,
              )}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}

function additionalKey(additional: MonitorAdditionalDTO) {
  return additional.name || additional.id;
}

function flattenAdditionalRows(
  snap: MonitorSnapDTO | undefined | null,
): AdditionalRow[] {
  const list = snap?.additionals;
  if (!Array.isArray(list)) return [];

  return list.map((additional) => ({
    key: additionalKey(additional),
    name: toStr(additional.name),
    description: toStr(additional.description),
    basePrice: toValueString(additional.basePrice),
    rentingFees:
      Object.entries(additional.rentingFees ?? {})
        .sort((left, right) => Number(left[0]) - Number(right[0]))
        .map(
          ([duration, price]) =>
            `${duration} ${t("Mon.")}: ${toValueString(price)}`,
        )
        .join("\n") || "—",
    image: additional.image,
  }));
}

function emptyAdditionalRow(key: string): AdditionalRow {
  return {
    key,
    name: "—",
    description: "—",
    basePrice: "—",
    rentingFees: "—",
    image: undefined,
  };
}

function AdditionalsDiffTable({
  current,
  proposed,
}: {
  current: MonitorSnapDTO;
  proposed: MonitorSnapDTO;
}) {
  const currentRows = flattenAdditionalRows(current);
  const proposedRows = flattenAdditionalRows(proposed);

  const byKeyCurrent = new Map(currentRows.map((r) => [r.key, r]));
  const byKeyProposed = new Map(proposedRows.map((r) => [r.key, r]));

  const keys = Array.from(
    new Set([...byKeyCurrent.keys(), ...byKeyProposed.keys()]),
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  const merged = keys.map((key) => ({
    key,
    current: byKeyCurrent.get(key) ?? emptyAdditionalRow(key),
    proposed:
      byKeyProposed.get(key) ??
      byKeyCurrent.get(key) ??
      emptyAdditionalRow(key),
  }));

  const renderFieldRow = (
    label: string,
    cur: DiffCellValue,
    prop: DiffCellValue,
    rowId: string,
  ) => {
    const currentValue = normalizeDiffValue(cur);
    const proposedValue = normalizeDiffValue(prop);
    const changed = currentValue.compare !== proposedValue.compare;
    const rowClassName = changed ? "bg-emerald-500/10" : undefined;
    const cellClassName = changed ? "font-medium" : undefined;

    return (
      <TableRow key={rowId} className={rowClassName}>
        <TableCell className="align-top font-medium">{label}</TableCell>
        <TableCell
          className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
        >
          {currentValue.render}
        </TableCell>
        <TableCell
          className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
        >
          {proposedValue.render}
        </TableCell>
      </TableRow>
    );
  };

  if (merged.length === 0) {
    return (
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-55">{t("Feld")}</TableHead>
              <TableHead>{t("Aktuell")}</TableHead>
              <TableHead>{t("Überschreiben mit")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={3}
                className="text-muted-foreground text-center"
              >
                {t("Keine Material-Zusätze")}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {merged.map(({ key, current: cur, proposed: prop }, idx) => (
        <div key={key} className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-55">{t("Feld")}</TableHead>
                <TableHead>{t("Aktuell")}</TableHead>
                <TableHead>{t("Überschreiben mit")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderFieldRow(t("Name"), cur.name, prop.name, `${idx}-name`)}
              {renderFieldRow(
                t("Katalogtext"),
                cur.description,
                prop.description,
                `${idx}-description`,
              )}
              {renderFieldRow(
                t("Preis"),
                cur.basePrice,
                prop.basePrice,
                `${idx}-base-price`,
              )}
              {renderFieldRow(
                t("Mietpreise"),
                cur.rentingFees,
                prop.rentingFees,
                `${idx}-renting-fees`,
              )}
              {renderFieldRow(
                t("Bild"),
                imageRefValue(cur.image),
                imageRefValue(prop.image),
                `${idx}-image`,
              )}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}

function buildSnapshotSections(
  current: MonitorSnapDTO,
  proposed: MonitorSnapDTO,
) {
  const monitorType = current.type;
  const core: DiffRow[] = [
    {
      label: t("Name"),
      current: toStr(current.name),
      proposed: toStr(proposed.name),
    },
    {
      label: t("Lieferant"),
      current: toStr(current.supplier),
      proposed: toStr(proposed.supplier),
    },
    {
      label: t("Beschreibung"),
      current: toStr(current.details?.description),
      proposed: toStr(proposed.details?.description),
    },
    {
      label: t("Monitorart"),
      current: toStr(MonitorTypeUtil.toLongString(current.type)),
      proposed: toStr(MonitorTypeUtil.toLongString(proposed.type)),
    },
    {
      label: t("Installationszeit"),
      current: toStr(current.times?.install),
      proposed: toStr(proposed.times?.install),
    },
    {
      label: t("Servicezeit"),
      current: toStr(current.times?.maintain),
      proposed: toStr(proposed.times?.maintain),
    },
    {
      label: t("Anzahl empfohlener Maßnahmen"),
      current: toStr(current.recommendedVisits),
      proposed: toStr(proposed.recommendedVisits),
    },
  ];

  const capKeys: { key: string; label: string }[] = [];
  if (monitorType === MonitorType.Checkpoint) {
    capKeys.push(
      { key: "permanentBaiting", label: t("Dauerbeköderung") },
      { key: "hasLethalAgent", label: t("Mit Wirkstoff") },
    );
  }
  if (
    monitorType === MonitorType.Checkpoint ||
    monitorType === MonitorType.FlyExterminator ||
    monitorType === MonitorType.IoT
  ) {
    capKeys.push({
      key: "includeInCheckpointService",
      label: t("Als QPM Service berechnen"),
    });
  }

  const capabilitiesRows: DiffRow[] = capKeys.map(({ key, label }) => ({
    label,
    current: toStr(getBool(current.capabilities, key)),
    proposed: toStr(getBool(proposed.capabilities, key)),
  }));

  if (monitorType === MonitorType.IoT) {
    const currentIot = getPath(current.capabilities, ["iot"]);
    const proposedIot = getPath(proposed.capabilities, ["iot"]);

    capabilitiesRows.push({
      label: t("IoT Protokoll"),
      current: toStr(getPath(currentIot, ["protocol"])),
      proposed: toStr(getPath(proposedIot, ["protocol"])),
    });

    capabilitiesRows.push({
      label: t("IoT Klasse"),
      current: toStr(getPath(currentIot, ["class"])),
      proposed: toStr(getPath(proposedIot, ["class"])),
    });
  }

  const defaultsRows: DiffRow[] = [
    {
      label: t("Preis"),
      current: toStr(
        (current.basePrice as unknown as { price?: number } | undefined)?.price,
      ),
      proposed: toStr(
        (proposed.basePrice as unknown as { price?: number } | undefined)
          ?.price,
      ),
    },
    {
      label: t("Preis gültig ab"),
      current: toStr(
        (current.basePrice as unknown as { effectiveOn?: string } | undefined)
          ?.effectiveOn,
      ),
      proposed: toStr(
        (proposed.basePrice as unknown as { effectiveOn?: string } | undefined)
          ?.effectiveOn,
      ),
    },
  ];

  const imageRows: DiffRow[] = [
    {
      label: t("Bild"),
      current: imageRefValue(current.image),
      proposed: imageRefValue(proposed.image),
    },
  ];

  const sections: SnapshotSectionKind[] = [
    { kind: "diff", title: t("Basisdaten"), rows: core },
    ...(capabilitiesRows.length > 0
      ? ([
          { kind: "diff", title: t("Funktionen"), rows: capabilitiesRows },
        ] as const)
      : []),
    { kind: "diff", title: t("Standardwerte"), rows: defaultsRows },
    { kind: "diff", title: t("Bild"), rows: imageRows },
    {
      kind: "details",
      title: t("Details"),
      rows: buildDetailsRows(current, proposed),
    },
    ...(MonitorTypeUtil.supportsRentingDefaults(monitorType)
      ? ([
          {
            kind: "renting",
            title: t("Mietkonditionen"),
            rows: buildRentingDefaultsRows(current, proposed),
          },
        ] as const)
      : []),
  ];

  return sections.filter((s) => s.rows.length > 0);
}

type PestRow = {
  name: string;
  abbreviation: string;
  description: string;
};

function flattenPestsByKind(
  snap: MonitorSnapDTO | undefined | null,
  kind: PestKind,
): PestRow[] {
  const list = snap?.pests;
  if (!Array.isArray(list)) return [];

  return list
    .filter((p) => p?.kind === kind)
    .map((p) => ({
      name: toStr(p.name),
      abbreviation: toStr(p.abbreviation),
      description: toStr(p.description),
    }));
}

function pestKey(p: PestRow) {
  return `${p.name}|${p.abbreviation}`;
}

function PestsDiffTable({
  current,
  proposed,
  kind,
  title,
}: {
  current: MonitorSnapDTO;
  proposed: MonitorSnapDTO;
  kind: PestKind;
  title: string;
}) {
  const currentRows = flattenPestsByKind(current, kind);
  const proposedRows = flattenPestsByKind(proposed, kind);

  const currentByKey = new Map(currentRows.map((r) => [pestKey(r), r]));
  const proposedByKey = new Map(proposedRows.map((r) => [pestKey(r), r]));

  const keys = Array.from(
    new Set([...currentByKey.keys(), ...proposedByKey.keys()]),
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  const merged = keys.map((k) => ({
    key: k,
    current: currentByKey.get(k) ?? {
      name: "—",
      abbreviation: "—",
      description: "—",
    },
    proposed: proposedByKey.get(k) ?? {
      name: "—",
      abbreviation: "—",
      description: "—",
    },
  }));

  const renderFieldRow = (
    label: string,
    cur: string,
    prop: string,
    rowId: string,
  ) => {
    const changed = cur !== prop;
    const rowClassName = changed ? "bg-emerald-500/10" : undefined;
    const cellClassName = changed ? "font-medium" : undefined;

    return (
      <TableRow key={rowId} className={rowClassName}>
        <TableCell className="align-top font-medium">{label}</TableCell>
        <TableCell
          className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
        >
          {cur}
        </TableCell>
        <TableCell
          className={`align-top wrap-break-word whitespace-pre-wrap ${cellClassName ?? ""}`}
        >
          {prop}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium">{title}</div>

      {merged.length === 0 ? (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-55">{t("Feld")}</TableHead>
                <TableHead>{t("Aktuell")}</TableHead>
                <TableHead>{t("Überschreiben mit")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-muted-foreground text-center"
                >
                  {t("Keine Einträge")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {merged.map(({ key, current: cur, proposed: prop }, idx) => (
            <div key={key} className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-55">{t("Feld")}</TableHead>
                    <TableHead>{t("Aktuell")}</TableHead>
                    <TableHead>{t("Überschreiben mit")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renderFieldRow(
                    t("Name"),
                    cur.name,
                    prop.name,
                    `${idx}-name`,
                  )}
                  {renderFieldRow(
                    t("Abkürzung"),
                    cur.abbreviation,
                    prop.abbreviation,
                    `${idx}-abbr`,
                  )}
                  {renderFieldRow(
                    t("Beschreibung"),
                    cur.description,
                    prop.description,
                    `${idx}-desc`,
                  )}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RefreshPreviewDiff({
  currentMonitorSnap,
  proposedSnapshot,
  oldHash,
  newHash,
}: {
  currentMonitorSnap: MonitorSnapDTO;
  proposedSnapshot: MonitorSnapDTO;
  oldHash?: string;
  newHash?: string;
}) {
  const monitorType = currentMonitorSnap.type;

  return (
    <div className="flex flex-col gap-4">
      {buildSnapshotSections(currentMonitorSnap, proposedSnapshot).map(
        (section) => (
          <div key={section.title} className="flex flex-col gap-2">
            <div className="text-sm font-medium">{section.title}</div>
            {section.kind === "diff" && <DiffTable rows={section.rows} />}
            {section.kind === "details" && <DetailsTable rows={section.rows} />}
            {section.kind === "renting" && (
              <RentingDefaultsTable rows={section.rows} />
            )}
          </div>
        ),
      )}

      <PestsDiffTable
        current={currentMonitorSnap}
        proposed={proposedSnapshot}
        kind={PestKind.Category}
        title={t("Schädlingskategorien")}
      />

      <PestsDiffTable
        current={currentMonitorSnap}
        proposed={proposedSnapshot}
        kind={PestKind.Indicator}
        title={t("Schädlingsindikatoren")}
      />

      {MonitorTypeUtil.supportsConsumables(monitorType) ? (
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">
            {t("Verbrauchsmaterialien")}
          </div>
          <ConsumablesDiffTable
            current={currentMonitorSnap}
            proposed={proposedSnapshot}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium">{t("Material-Zusätze")}</div>
        <AdditionalsDiffTable
          current={currentMonitorSnap}
          proposed={proposedSnapshot}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium">{t("Hash Werte")}</div>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-55">{t("Feld")}</TableHead>
                <TableHead>{t("Wert")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="align-top font-medium">
                  {t("Alter Hash Wert")}
                </TableCell>
                <TableCell className="align-top wrap-break-word whitespace-pre-wrap">
                  {oldHash || "--"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="align-top font-medium">
                  {t("Neuer Hash Wert")}
                </TableCell>
                <TableCell className="align-top wrap-break-word whitespace-pre-wrap">
                  {newHash || "--"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
